import { ArrowDownIcon, ArrowUpIcon, MinusIcon } from "lucide-react";

import { Row } from "@/components/Row";
import { Text } from "@/components/Text";

import { sadieDashboardRangeToDays } from "@/lib/constants";
import { sadieDashboard } from "@/lib/sadieDashboard";
import { cn } from "@/lib/utils";
import {
  SadieDashboardRange,
  SadieDashboardStat,
  SadieStatFormat,
} from "@/types";

// How a number moved since the window before it. Green is good news, which
// for most numbers is up, but for an opt-out rate is down.
export const SadieStatDelta = ({
  decimals = 0,
  format,
  isOnDark = false,
  isUpGood = true,
  range,
  stat,
}: {
  decimals?: number;
  format: SadieStatFormat;
  isOnDark?: boolean;
  isUpGood?: boolean;
  range: SadieDashboardRange;
  stat: SadieDashboardStat;
}) => {
  /* Helpers */
  const change = sadieDashboard.change(stat, format, decimals);
  const comparison = `vs previous ${sadieDashboardRangeToDays[range].toString()} days`;
  const neutralClass = isOnDark ? "text-slate-400" : "text-slate-500";
  const goodClass = isOnDark ? "text-emerald-400" : "text-emerald-600";
  const badClass = isOnDark ? "text-rose-400" : "text-rose-600";

  /* Render */
  const render = () => {
    if (!change) {
      return (
        <Text className={cn("font-plex-mono text-[11px]", neutralClass)}>
          No earlier data
        </Text>
      );
    }

    if (change.amount === 0) {
      return (
        <Row className={cn("items-center gap-1", neutralClass)}>
          <MinusIcon className="h-3 w-3 shrink-0" />
          <Text className="font-plex-mono text-[11px] text-inherit">
            No change {comparison}
          </Text>
        </Row>
      );
    }

    const isUp = change.amount > 0;
    const DeltaIcon = isUp ? ArrowUpIcon : ArrowDownIcon;
    return (
      <Row
        className={cn(
          "items-center gap-1",
          isUp === isUpGood ? goodClass : badClass,
        )}
      >
        <DeltaIcon className="h-3 w-3 shrink-0" />
        <Text className="font-plex-mono text-[11px] font-medium text-inherit">
          {change.label} {comparison}
        </Text>
      </Row>
    );
  };

  return render();
};
